/**
 * ReleaseUpdatePrompt -- "a new version is available" banner (#4243).
 *
 * Mounted once at the app root. It checks /version.json (see
 * lib/releaseCheck) when the tab regains focus or becomes visible, on route
 * change and on a slow interval. When a newer build has been deployed it shows
 * a small banner with a Reload button. It never reloads on its own, so input
 * in an open form is never lost; dismissing it hides it until a later release.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { RefreshCw, X } from "lucide-react";
import {
  RELEASE_CHECK_INTERVAL_MS,
  createReleaseChecker,
  runningBuildId,
} from "../lib/releaseCheck";

interface Props {
  /** Test seam: the running build id (defaults to the compiled-in one). */
  buildId?: string;
  /** Test seam: the fetch used for /version.json. */
  fetchImpl?: typeof fetch;
}

export function ReleaseUpdatePrompt({ buildId, fetchImpl }: Props) {
  const running = buildId ?? runningBuildId();
  const checker = useMemo(
    () => createReleaseChecker({ running, fetchImpl }),
    [running, fetchImpl],
  );
  const location = useLocation();
  const [available, setAvailable] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);

  useEffect(() => {
    if (!running) return;
    let cancelled = false;
    const run = () => {
      void checker.check().then((deployed) => {
        if (!cancelled && deployed) setAvailable(deployed);
      });
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") run();
    };
    window.addEventListener("focus", run);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(run, RELEASE_CHECK_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", run);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [checker, running]);

  // Route change: the throttle keeps this to one request a minute at most.
  useEffect(() => {
    if (!running) return;
    let cancelled = false;
    void checker.check().then((deployed) => {
      if (!cancelled && deployed) setAvailable(deployed);
    });
    return () => {
      cancelled = true;
    };
  }, [checker, running, location.pathname]);

  if (!available || available === dismissed) return null;

  return (
    <div
      className="fixed inset-x-0 top-0 z-[9000] flex justify-center px-3 pt-3 pointer-events-none"
      role="status"
      aria-live="polite"
    >
      <div className="pointer-events-auto w-full sm:w-auto sm:max-w-md rounded-lg bg-[var(--color-bg-card)] border border-[var(--color-border)] border-l-[3px] border-l-brand-accent shadow-lg">
        <div className="flex items-start gap-3 p-4">
          <RefreshCw className="h-5 w-5 mt-0.5 shrink-0 text-brand-accent" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-[var(--color-text-primary)]">
              A new version is available
            </p>
            <p className="mt-0.5 text-sm text-[var(--color-text-secondary)]">
              Save any open work, then reload to get the latest fixes.
            </p>
            {/* `text-[#ffffff]`, not `text-white`: `.dark` redefines
                --color-white, and the accent stays blue in both themes. */}
            <div className="mt-3 flex flex-col-reverse gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => setDismissed(available)}
                className="min-h-[44px] sm:min-h-0 px-3 py-2 rounded-lg border border-[var(--color-border)] text-sm font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] transition-colors"
              >
                Later
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="min-h-[44px] sm:min-h-0 px-3 py-2 rounded-lg bg-brand-accent text-[#ffffff] text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Reload now
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setDismissed(available)}
            aria-label="Dismiss"
            className="shrink-0 -mr-1 -mt-1 flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 sm:p-1.5 rounded-lg text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default ReleaseUpdatePrompt;
